import type { AmenityIconKey } from '@/lib/amenityIcons';
import {
  ApiRequestError,
  apiJson,
  apiJsonArray,
} from '@/lib/api-client';

export interface DevelopmentAmenity {
  name: string;
  description?: string;
  icon?: AmenityIconKey;
}

export interface Development {
  id: number;
  name: string;
  link_interno?: string;
  description?: string;
  address?: string;
  city?: string;
  state?: string;
  website?: string;
  email?: string;
  phone?: string;
  logo?: string;
  check_in_time?: string;
  check_out_time?: string;
  house_rules?: string;
  additional_info?: string;
  photos?: string[] | string;
  cover_image?: string;
  amenities?: DevelopmentAmenity[] | string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateDevelopment {
  name: string;
  link_interno?: string;
  description?: string;
  address?: string;
  city?: string;
  state?: string;
  website?: string;
  email?: string;
  phone?: string;
  logo?: string;
  check_in_time?: string;
  check_out_time?: string;
  house_rules?: string;
  additional_info?: string;
  photos?: string[] | string;
  cover_image?: string;
  amenities?: DevelopmentAmenity[] | string;
  is_active?: boolean;
}

export interface UpdateDevelopment {
  name?: string;
  link_interno?: string;
  description?: string;
  address?: string;
  city?: string;
  state?: string;
  website?: string;
  email?: string;
  phone?: string;
  logo?: string;
  check_in_time?: string;
  check_out_time?: string;
  house_rules?: string;
  additional_info?: string;
  photos?: string[] | string;
  cover_image?: string;
  amenities?: DevelopmentAmenity[] | string;
  is_active?: boolean;
}

/**
 * Converte o campo `amenities` (array ou string JSON) numa lista tipada.
 * Retorna sempre um array (vazio quando inválido/nulo).
 *
 * Cada item normalizado contém `name` (obrigatório), `description` (opcional)
 * e `icon` (opcional — chave de `AMENITY_ICONS`).
 */
export function parseDevelopmentAmenities(
  raw: Development['amenities'] | undefined,
): DevelopmentAmenity[] {
  const normalize = (value: unknown): DevelopmentAmenity | null => {
    if (!value || typeof value !== 'object') return null;
    const obj = value as Record<string, unknown>;
    const name = typeof obj.name === 'string' ? obj.name.trim() : '';
    if (!name) return null;
    const description =
      typeof obj.description === 'string' && obj.description.trim() !== ''
        ? obj.description.trim()
        : undefined;
    const icon =
      typeof obj.icon === 'string' && obj.icon.trim() !== ''
        ? (obj.icon.trim() as AmenityIconKey)
        : undefined;
    return { name, description, icon };
  };

  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map(normalize)
      .filter((item): item is DevelopmentAmenity => item !== null);
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed
          .map(normalize)
          .filter((item): item is DevelopmentAmenity => item !== null);
      }
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Converte `developments.photos` (JSON no MySQL, string JSON aninhada, ou itens objeto)
 * numa lista de paths/URLs em ordem. Usado na home e na página do resort.
 */
export function parseDevelopmentPhotos(
  raw: Development['photos'] | undefined,
): string[] {
  const pathsFromArray = (arr: unknown[]): string[] => {
    const out: string[] = [];
    for (const item of arr) {
      if (typeof item === 'string' && item.trim() !== '') {
        out.push(item.trim());
        continue;
      }
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        const o = item as Record<string, unknown>;
        const pathVal = o.url ?? o.path ?? o.src ?? o.filename ?? o.file;
        if (typeof pathVal === 'string' && pathVal.trim() !== '') {
          out.push(pathVal.trim());
        }
      }
    }
    return out;
  };

  const objectToPseudoArray = (obj: Record<string, unknown>): unknown[] | null => {
    const keys = Object.keys(obj);
    if (keys.length === 0) return null;
    const numericKeys = keys.filter((k) => /^\d+$/.test(k));
    if (numericKeys.length !== keys.length) return null;
    return numericKeys.sort((a, b) => Number(a) - Number(b)).map((k) => obj[k]);
  };

  let cur: unknown = raw as unknown;

  for (let depth = 0; depth < 8; depth++) {
    if (cur == null || cur === '') return [];

    if (Array.isArray(cur)) {
      return pathsFromArray(cur);
    }

    if (typeof cur === 'object') {
      const obj = cur as Record<string, unknown>;
      if (Array.isArray(obj.paths)) {
        cur = obj.paths;
        continue;
      }
      const pseudo = objectToPseudoArray(obj);
      if (pseudo) {
        cur = pseudo;
        continue;
      }
      return [];
    }

    if (typeof cur === 'string') {
      const t = cur.trim();
      if (!t) return [];
      try {
        cur = JSON.parse(t);
        continue;
      } catch {
        if (
          t.startsWith('/') ||
          t.startsWith('http://') ||
          t.startsWith('https://') ||
          /\.(jpe?g|png|gif|webp|svg)(\?[^#]*)?$/i.test(t) ||
          t.includes('/')
        ) {
          return [t];
        }
        return [];
      }
    }

    return [];
  }

  return [];
}

export class DevelopmentService {
  /**
   * Busca todos os empreendimentos ativos
   */
  static async getAllDevelopments(): Promise<Development[]> {
    return apiJsonArray<Development>('api/developments');
  }

  /**
   * Busca todos os empreendimentos (ativos e inativos) para administração
   */
  static async getAllDevelopmentsForAdmin(): Promise<Development[]> {
    return apiJsonArray<Development>('api/developments/admin');
  }

  /**
   * Busca um empreendimento específico por ID
   */
  static async getDevelopmentById(id: number): Promise<Development | null> {
    try {
      return await apiJson<Development>(`api/developments/${id}`);
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  static async getDevelopmentBySlug(slug: string): Promise<Development | null> {
    try {
      return await apiJson<Development>(
        `api/developments/slug/${encodeURIComponent(slug)}`,
      );
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Cria um novo empreendimento
   */
  static async createDevelopment(developmentData: CreateDevelopment): Promise<Development> {
    const result = await apiJson<{ id: number }>('api/developments', {
      method: 'POST',
      body: JSON.stringify(developmentData),
    });
    const createdDevelopment = await this.getDevelopmentById(result.id);
    if (!createdDevelopment) {
      throw new Error('Empreendimento criado mas não foi possível recuperá-lo');
    }
    return createdDevelopment;
  }

  /**
   * Atualiza um empreendimento existente
   */
  static async updateDevelopment(
    id: number,
    developmentData: UpdateDevelopment,
  ): Promise<Development> {
    await apiJson(`api/developments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(developmentData),
    });
    const updatedDevelopment = await this.getDevelopmentById(id);
    if (!updatedDevelopment) {
      throw new Error('Empreendimento atualizado mas não foi possível recuperá-lo');
    }
    return updatedDevelopment;
  }

  /**
   * Exclui um empreendimento
   */
  static async deleteDevelopment(id: number): Promise<void> {
    await apiJson(`api/developments/${id}`, { method: 'DELETE' });
  }

  /**
   * Alterna o status ativo/inativo de um empreendimento
   */
  static async toggleDevelopmentStatus(id: number): Promise<Development> {
    await apiJson(`api/developments/${id}/toggle-status`, { method: 'PATCH' });
    const updatedDevelopment = await this.getDevelopmentById(id);
    if (!updatedDevelopment) {
      throw new Error('Status alterado mas não foi possível recuperar o empreendimento');
    }
    return updatedDevelopment;
  }

  static async getOwnerIdsForDevelopment(developmentId: number): Promise<{
    developmentId: number;
    developmentName: string;
    ownerIds: number[];
  }> {
    return apiJson(`api/developments/${developmentId}/owner-ids`);
  }
}

